import React from "react";
import { Check, ArrowUpRight } from "lucide-react";
import TrialCapture from "@/components/sections/TrialCapture";

const baseFeatures = [
    "Full desktop application (Windows)",
    "Google Places search · multi-business-type",
    "Saved searches + Load More",
    "Website email discovery + trust scoring",
    "Duplicate consolidation",
    "CSV / Excel import",
    "Contact page + form helper",
    "Rich email editor + test-first sending",
    "Bring-your-own SMTP (Google, Resend, any)",
    "Suppression + unsubscribe handling",
    "Activity timeline per business",
];

const proExtras = [
    "WhatsApp handoff with pacing controls",
    "Provider quota guard + bounce / complaint visibility",
    "Priority email support",
    "Advanced diagnostics + log export",
];

const agencyExtras = [
    "Everything in Professional",
    "Support for agency workflows",
    "Multi-user onboarding guidance",
    "Priority support for active campaigns",
    "Best for teams managing multiple clients",
];

const plans = [
    {
        id: "free-month",
        checkoutPlan: "free_month",
        tag: "Free Month",
        sub: "30 days full access, then Launch Monthly unless cancelled.",
        monthly: "£29",
        yearly: "£0",
        computers: "1 computer total",
        highlight: true,
        badge: "30 days",
        features: [
            "Full ReachForge Leads access",
            "Card required upfront",
            "£0 today",
            "Automatically moves to Launch Monthly",
            "Cancel before trial end to avoid billing",
            ...baseFeatures.slice(1, 6),
        ],
        extras: [],
        cta: "Start Free Month",
    },
    {
        id: "launch",
        checkoutPlan: "launch_annual",
        tag: "Launch / Solo",
        sub: "For independent operators and starter teams.",
        monthly: "£29",
        yearly: "£249",
        computers: "Supports 1 computer total",
        highlight: false,
        features: baseFeatures,
        extras: [],
        cta: "Get Launch",
    },
    {
        id: "professional",
        checkoutPlan: "professional_annual",
        tag: "Professional",
        sub: "For agencies and teams prospecting every day.",
        monthly: "£49",
        yearly: "£449",
        computers: "Supports 3 computers total",
        highlight: false,
        features: baseFeatures,
        extras: proExtras,
        cta: "Get Professional",
    },
    {
        id: "agency",
        checkoutPlan: "agency_annual",
        tag: "Agency",
        sub: "For multi-client workflows and larger prospecting teams.",
        monthly: "£79",
        yearly: "£749",
        computers: "Supports 5 computers total",
        highlight: false,
        features: baseFeatures,
        extras: agencyExtras,
        cta: "Get Agency",
    },
];

export default function Pricing() {
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="border-b border-border bg-ink text-bg"
        >
            <TrialCapture />
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <span
                            className="font-mono uppercase tracking-[0.2em] text-xs"
                            style={{ color: "hsl(var(--ink-mute))" }}
                        >
                            Pricing
                        </span>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-bg">
                            Start free. Scale when ready.
                        </h2>
                    </div>
                    <p className="text-bg/70 max-w-md">
                        Free Month starts with £0 today and card details
                        upfront, then converts to Launch Monthly after 30 days
                        unless cancelled.
                    </p>
                </div>

                <div className="grid lg:grid-cols-4 grid-skeleton border border-bg/20">
                    {plans.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`pricing-plan-${p.id}`}
                            className={`p-8 md:p-10 lg:p-12 flex flex-col ${
                                p.highlight ? "bg-bg text-ink" : "bg-ink text-bg"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <span
                                    className={`font-mono uppercase tracking-[0.2em] text-xs ${
                                        p.highlight
                                            ? "text-ink-mute"
                                            : "text-bg/60"
                                    }`}
                                >
                                    {p.tag}
                                </span>
                                {p.highlight && (
                                    <span className="bg-forge text-bg px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase">
                                        {p.badge || "Best seller"}
                                    </span>
                                )}
                            </div>
                            <p
                                className={`mt-3 text-sm ${
                                    p.highlight ? "text-ink-soft" : "text-bg/70"
                                }`}
                            >
                                {p.sub}
                            </p>
                            <p
                                className={`mt-4 inline-flex w-fit border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] ${
                                    p.highlight
                                        ? "border-border text-ink-mute"
                                        : "border-bg/20 text-bg/60"
                                }`}
                            >
                                {p.computers}
                            </p>
                            <div className="flex items-end gap-2 mt-7">
                                <div className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none">
                                    {p.yearly}
                                </div>
                                <div
                                    className={`pb-2 ${
                                        p.highlight
                                            ? "text-ink-soft"
                                            : "text-bg/60"
                                    }`}
                                >
                                    {p.id === "free-month" ? "today" : "/year"}
                                </div>
                            </div>
                            <p
                                className={`mt-2 text-sm font-mono uppercase tracking-[0.2em] ${
                                    p.highlight ? "text-ink-mute" : "text-bg/50"
                                }`}
                            >
                                {p.id === "free-month"
                                    ? "Then £29 per month"
                                    : `Or ${p.monthly} per month`}
                            </p>

                            <ul className="mt-8 space-y-2.5">
                                {p.features.map((m) => (
                                    <li
                                        key={m}
                                        className="flex items-start gap-3 text-sm"
                                    >
                                        <Check
                                            size={14}
                                            className="text-forge mt-1 shrink-0"
                                            strokeWidth={3}
                                        />
                                        <span
                                            className={
                                                p.highlight
                                                    ? "text-ink-soft"
                                                    : "text-bg/85"
                                            }
                                        >
                                            {m}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {p.extras.length > 0 && (
                                <>
                                    <div
                                        className={`mt-7 pt-5 border-t font-mono uppercase tracking-[0.2em] text-[10px] ${
                                            p.highlight
                                                ? "border-border text-ink-mute"
                                                : "border-bg/20 text-bg/50"
                                        }`}
                                    >
                                        Plus, only in {p.tag}
                                    </div>
                                    <ul className="mt-3 space-y-2.5">
                                        {p.extras.map((m) => (
                                            <li
                                                key={m}
                                                className="flex items-start gap-3 text-sm"
                                            >
                                                <Check
                                                    size={14}
                                                    className="text-forge mt-1 shrink-0"
                                                    strokeWidth={3}
                                                />
                                                <span
                                                    className={
                                                        p.highlight
                                                            ? "text-ink font-medium"
                                                            : "text-bg font-medium"
                                                    }
                                                >
                                                    {m}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            <a
                                href={`/checkout?plan=${p.checkoutPlan}`}
                                data-testid={`pricing-cta-${p.id}`}
                                className={`btn mt-10 ${
                                    p.highlight ? "btn-accent" : "btn-secondary"
                                }`}
                            >
                                {p.cta} <ArrowUpRight size={14} />
                            </a>
                        </div>
                    ))}
                </div>

                <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-bg/50">
                    Requires Windows · Your own Google Places API key · Your own
                    SMTP (Google Workspace, Resend, or any)
                </p>
            </div>
        </section>
    );
}
